import React, { useCallback, useEffect, useState } from "react";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import SearchableTable from "../../../components/Tables/SearchableTable";
import AuthInput from "../../../components/Input/AuthInput";
import AuthBtn from "../../../components/Buttons/AuthBtn";
import CustomModal from "../../../components/Modals/CustomModal";
import {
  GetLoanAccountUsersApi,
  UpdateLoanAccountUserApi,
} from "../../../ApiRequests";
import { ErrorToast, SuccessToast, WarningToast } from "../../../utils/ShowToast";
import { usePageRefresh } from "../../../hooks/usePageRefresh";

const LoanAccountUsersColumns = [
  { id: "partyName", title: "Party" },
  { id: "email", title: "Login email" },
  { id: "phone", title: "Phone" },
  { id: "actions", title: "Actions" },
];

const LoanAccountUsers = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetLoanAccountUsersApi();
      const users = res?.data?.data?.payload || [];
      setRows(
        users.map((u) => ({
          _id: u._id,
          partyName: u.loanAccountId?.partyName || u.name || "—",
          phone: u.loanAccountId?.phone || "—",
          email: u.email,
          raw: u,
        }))
      );
    } catch (e) {
      ErrorToast(e.response?.data?.error?.msg || "Failed to load portal users");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  usePageRefresh(loadUsers);

  useEffect(() => {
    if (openEditModal && selected) {
      setEmail(selected.email || "");
      setPassword("");
    }
  }, [openEditModal, selected]);

  const onSavePortalLogin = async (e) => {
    e.preventDefault();
    if (!selected?._id) return;

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      WarningToast("Email is required");
      return;
    }
    if (password && password.length < 8) {
      WarningToast("Password must be at least 8 characters");
      return;
    }

    setSaving(true);
    try {
      const payload = { email: trimmedEmail };
      if (password.trim()) payload.password = password.trim();

      const res = await UpdateLoanAccountUserApi(selected._id, payload);
      if (res.data.success) {
        SuccessToast(
          password.trim()
            ? "Portal email and password updated"
            : "Portal email updated"
        );
        setOpenEditModal(false);
        setPassword("");
        setSelected(null);
        loadUsers();
      } else {
        ErrorToast(res.data.error?.msg || "Update failed");
      }
    } catch (err) {
      ErrorToast(err.response?.data?.error?.msg || "Update failed");
    }
    setSaving(false);
  };

  return (
    <BodyWrapper>
      <Header
        title="Loan party portals"
        desc="Read-only logins (role 4) — auto-created when a new loan party is added"
      />

      <p className="text-sm text-inkSecondary mb-4 max850:text-xs">
        Edit icon se portal email change karo. Naya password optional hai — khali
        chhoro to sirf email update hogi.
      </p>

      {loading ? (
        <FetchingLoading />
      ) : (
        <SearchableTable
          SearchPlaceholder="Search party or email…"
          SearchText={searchText}
          setSearchText={setSearchText}
          CurrentData={rows}
          Columns={LoanAccountUsersColumns}
          setOpenEditModal={setOpenEditModal}
          setSelected={setSelected}
        />
      )}

      {openEditModal && selected && (
        <CustomModal open={openEditModal} setOpen={setOpenEditModal}>
          <div className="pb-8 px-8 max696:px-4">
            <h2 className="text-xl font-bold text-center py-6 text-ink">
              Edit portal login
            </h2>
            <p className="text-sm text-inkSecondary text-center mb-4">
              {selected.partyName}
            </p>
            <AuthInput
              label="Login email"
              Type="email"
              Value={email}
              setValue={setEmail}
              placeholder="party@example.com"
            />
            <AuthInput
              label="New password (optional)"
              Type="password"
              Value={password}
              setValue={setPassword}
              placeholder="8–15 letters and numbers"
            />
            <p className="text-xs text-muted mt-2 text-center">
              Password khali chhoro agar sirf email change karni ho.
            </p>
            <div className="flex justify-center gap-2 mt-6">
              <AuthBtn
                title={saving ? "Saving…" : "Save changes"}
                onSubmit={onSavePortalLogin}
                type={1}
              />
              <AuthBtn
                title="Cancel"
                onSubmit={() => {
                  setOpenEditModal(false);
                  setPassword("");
                }}
                type={2}
              />
            </div>
          </div>
        </CustomModal>
      )}
    </BodyWrapper>
  );
};

export default LoanAccountUsers;

